"use client";

import React, {
  createContext,
  useContext,
  useRef,
  ReactNode,
  RefObject,
} from "react";

type SearchFocusContextType = {
  /** Register an input under a given key (e.g. label) */
  registerInput: (key: string, ref: RefObject<HTMLInputElement>) => void;
  focusInput: (key: string) => void;
};

const defaultValue: SearchFocusContextType = {
  registerInput: () => {},
  focusInput: () => {},
};

const SearchFocusContext = createContext<SearchFocusContextType>(defaultValue);

export function SearchFocusProvider({ children }: { children: ReactNode }) {
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});

  const registerInput = (key: string, ref: RefObject<HTMLInputElement>) => {
    inputs.current[key] = ref.current;
  };

  const focusInput = (key: string) => {
    const el = inputs.current[key];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.focus();
  };

  return (
    <SearchFocusContext.Provider value={{ registerInput, focusInput }}>
      {children}
    </SearchFocusContext.Provider>
  );
}

export function useSearchFocus(): SearchFocusContextType {
  return useContext(SearchFocusContext);
}
